import type { PayPalCheckout } from 'braintree-web/paypal-checkout';

export type PaypalEnvironment = 'sandbox' | 'production';

export const PaymentMethodType = {
  CARD: 'CARD',
  DEFERRED: 'DEFERRED',
  PAYPAL: 'PAYPAL',
  ACH: 'ACH',
} as const;

export type PaymentMethodType = typeof PaymentMethodType[keyof typeof PaymentMethodType];

export type PaypalInstanceType = {
  paypalInstance: PayPalCheckout | null;
  isPaypalButtonRendered: boolean;
  isPaypalLoadingError: boolean;
};

export type PaypalCallbacks = {
  onPaypalApprove: ({
    paypalPayload,
  }: {
    paypalPayload: AuthorizationResponse;
  }) => Promise<void> | void | { paypalPayload: AuthorizationResponse };

  onPaypalCancel?: () => void;

  onPaypalError?: (error: any) => void;

  onPaypalClick?: () => void;

  onPaypalButtonRender?: () => void;
};

export type PaypalButtonConfig = {
  env: PaypalEnvironment;
  displayName?: string;
  locale?: string;
  style?: ButtonStyle;
};

export interface AuthorizationData {
  payerId: string;
  paymentId?: string | undefined;
  billingToken?: string | undefined;
  vault?: boolean | undefined;
}
export type AuthorizationResponse = {
  nonce: string;
  type: string;
  details: AuthorizationResponseDetails;
};

export type CreatePaymentMethodContext = {
  paymentMethodType: PaymentMethodType;
  paypal?: {
    paypalDetails: AuthorizationResponse;
  };
};

type Address = {
  line1: string;
  line2?: string | undefined;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  phone?: string | undefined;
  recipientName?: string | undefined;
};

type AuthorizationResponseDetails = {
  email: string;
  payerId: string;
  firstName: string;
  lastName: string;
  countryCode?: string | undefined;
  phone?: string | undefined;
  shippingAddress?: Address | undefined;
  billingAddress?: Address | undefined;
  creditFinancingOffered?: CreditFinancingOptions | undefined;
};

type CurrencyAmount = {
  currency: string;
  value: string;
};

type CreditFinancingOptions = {
  totalCost: CurrencyAmount;
  term: number;
  monthlyPayment: CurrencyAmount;
  totalInterest: CurrencyAmount;
  payerAcceptance: boolean;
  cartAmountImmutable: boolean;
};

export enum Environment {
  Production = 'production',
  Sandbox = 'sandbox',
}

export enum ButtonColorOption {
  Gold = 'gold',
  Blue = 'blue',
  Silver = 'silver',
  Black = 'black',
  White = 'white',
}

export enum ButtonShapeOption {
  Pill = 'pill',
  Rect = 'rect',
}

export enum ButtonSizeOption {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Responsive = 'responsive',
}

export enum ButtonLabelOption {
  Checkout = 'checkout',
  Credit = 'credit',
  Pay = 'pay',
  BuyNow = 'buynow',
  PayPal = 'paypal',
}

export interface ButtonStyle {
  color?: ButtonColorOption;
  label?: ButtonLabelOption;
  shape?: ButtonShapeOption;
  size?: ButtonSizeOption;
  tagline?: boolean;
  height?: number;
}
